// frontend/src/pages/Conciencia.js
import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useAgentCommands } from '../contexts/AgentCommandContext';
import {
  generateConcienciaQuestion,
  postConcienciaResponse,
} from '../api';
import SphereMenu from '../components/SphereMenu';
import EmotionSphere from '../components/EmotionSphere';
import RateLimitWarning from '../components/RateLimitWarning';
import { AuthContext } from '../context/AuthContext';
import { useProgression } from '../context/ProgressionContext';
import { useNavigate } from 'react-router-dom';
import './Conciencia.css';
import MentalLoadScreen from '../components/MentalLoadScreen';
import ProfileBackground from '../components/ProfileBackground';
import { useAppBackground } from '../context/AppBackgroundContext';
import ReadableText from '../components/ReadableText';
import ConcienciaMazeGame, { createMazeTemplate, getMazeDifficulty, normalizeMazeTemplate } from '../components/conciencia/ConcienciaMazeGame';
import ConcienciaLetterPanel from '../components/conciencia/ConcienciaLetterPanel';
import { useI18n } from '../i18n';

const VIEW_STATE = {
  LOADING_INITIAL: 'loadingInitial',
  READY: 'ready',
  ZERO_ENERGY_MAZE: 'zeroEnergyMaze',
  MAZE_PLAY: 'mazePlay',
  ZERO_ENERGY_LETTER: 'zeroEnergyLetter',
  COOLDOWN: 'cooldown',
  ERROR: 'error'
};

const EMPTY_DATA = {
  question: '',
  questionReflection: '',
  opciones: [],
  inspiracionOpciones: []
};

const STAMINA_MAX = 100;
const LOCAL_UNLOCK_ENERGY = 20;
const STAMINA_RECHARGE_TAU_SECONDS = 150;
const STAMINA_LOW_RECHARGE_SLOWDOWN_THRESHOLD = 35;
const STAMINA_LOW_RECHARGE_SLOWDOWN_MAX = 1.18;
const STAMINA_FULL_THRESHOLD = 99.5;
const STAMINA_TICK_MS = 2000;
const DEFAULT_COOLDOWN_SECONDS = 60;
const MAX_COOLDOWN_SECONDS = 120;
const UNLOCK_LOOP_GUARD_MS = 90 * 1000;
const MAX_STORED_LETTERS = 20;
const MAX_STORED_MAZE_HISTORY = 20;
const MAZE_COMPLEXITY_TO_DIFFICULTY = { low: 1, medium: 2, high: 4, extreme: 7 };
const LETTER_TITLE_LIMIT = 80;
const LETTER_BODY_LIMIT = 900;
const MIN_STAMINA_DRAIN = 11;
const MAX_STAMINA_DRAIN = 42;

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function randomInt(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function getConcienciaStaminaDrain(lastQuestionLoadMs = 0) {
  const roll = Math.random();
  let drain;
  if (roll < 0.08) drain = randomInt(11, 20);
  else if (roll < 0.25) drain = randomInt(21, 27);
  else if (roll < 0.82) drain = randomInt(28, 38);
  else drain = randomInt(39, 42);

  const loadPenalty = lastQuestionLoadMs > 12000 ? 6 : lastQuestionLoadMs > 8000 ? 4 : lastQuestionLoadMs > 5000 ? 2 : 0;
  return clampNumber(drain + loadPenalty, MIN_STAMINA_DRAIN, MAX_STAMINA_DRAIN);
}

function normalizeStaminaValue(value, fallback = STAMINA_MAX) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? clampNumber(parsed, 0, STAMINA_MAX) : fallback;
}

function computeRechargedStamina(value, updatedAt, now = Date.now()) {
  const safeValue = normalizeStaminaValue(value);
  const safeUpdatedAt = Number(updatedAt);
  const elapsedSeconds = Math.max(0, (now - (Number.isFinite(safeUpdatedAt) ? safeUpdatedAt : now)) / 1000);
  if (elapsedSeconds <= 0 || safeValue >= STAMINA_MAX) return safeValue;

  const lowStaminaRatio = safeValue < STAMINA_LOW_RECHARGE_SLOWDOWN_THRESHOLD
    ? 1 - safeValue / STAMINA_LOW_RECHARGE_SLOWDOWN_THRESHOLD
    : 0;
  const lowStaminaTauMultiplier = 1 + lowStaminaRatio * (STAMINA_LOW_RECHARGE_SLOWDOWN_MAX - 1);
  const adjustedTau = STAMINA_RECHARGE_TAU_SECONDS * lowStaminaTauMultiplier;
  const next = STAMINA_MAX - (STAMINA_MAX - safeValue) * Math.exp(-elapsedSeconds / adjustedTau);
  return next >= STAMINA_FULL_THRESHOLD ? STAMINA_MAX : clampNumber(next, 0, STAMINA_MAX);
}

function readStoredStamina(userId) {
  const now = Date.now();
  const stored = safeReadJson(storageKey(userId, 'stamina_state'), null);
  if (!stored || typeof stored !== 'object') return { value: STAMINA_MAX, updatedAt: now };
  return {
    value: normalizeStaminaValue(stored.value),
    updatedAt: Number.isFinite(Number(stored.updatedAt)) ? Number(stored.updatedAt) : now
  };
}

function persistStamina(userId, value, updatedAt = Date.now()) {
  const safeValue = normalizeStaminaValue(value);
  const safeUpdatedAt = Number.isFinite(Number(updatedAt)) ? Number(updatedAt) : Date.now();
  safeSetJson(storageKey(userId, 'stamina_state'), { value: safeValue, updatedAt: safeUpdatedAt });
  return { value: safeValue, updatedAt: safeUpdatedAt };
}

function storageKey(userId, name) {
  return 'conciencia_' + name + '_' + userId;
}

function truncateText(value, limit) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (text.length <= limit) return text;
  return text.slice(0, Math.max(0, limit - 1)).trim() + '…';
}

function safeReadJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (_) {
    return fallback;
  }
}

function safeSetJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (_) {
    return false;
  }
}

function safeGetNumber(key, fallback = 0) {
  try {
    const parsed = Number(localStorage.getItem(key));
    return Number.isFinite(parsed) ? parsed : fallback;
  } catch (_) {
    return fallback;
  }
}

function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, String(value));
    return true;
  } catch (_) {
    return false;
  }
}

function safeRemoveItem(key) {
  try {
    localStorage.removeItem(key);
  } catch (_) {}
}

function normalizeCooldownUntil(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > Date.now() ? parsed : null;
}

function getCooldownUntil(userId) {
  return normalizeCooldownUntil(safeGetNumber(storageKey(userId, 'cooldown_until'), 0));
}

function getCooldownStartedAt(userId, cooldownUntil) {
  const stored = safeGetNumber(storageKey(userId, 'cooldown_started_at'), 0);
  if (stored > 0 && stored < cooldownUntil) return stored;
  return cooldownUntil - DEFAULT_COOLDOWN_SECONDS * 1000;
}

function getSafeCooldownUntil(seconds = DEFAULT_COOLDOWN_SECONDS) {
  const safeSeconds = Math.min(MAX_COOLDOWN_SECONDS, Math.max(1, Number(seconds) || DEFAULT_COOLDOWN_SECONDS));
  return Date.now() + safeSeconds * 1000;
}

function loadLetters(userId) {
  const letters = safeReadJson(storageKey(userId, 'letters'), []);
  return Array.isArray(letters)
    ? letters
        .filter((item) => item && typeof item === 'object')
        .map((item) => ({
          id: truncateText(item.id || String(Date.now()), 80),
          title: truncateText(item.title, LETTER_TITLE_LIMIT),
          body: truncateText(item.body, LETTER_BODY_LIMIT),
          reason: truncateText(item.reason, 40),
          createdAt: item.createdAt || new Date().toISOString()
        }))
        .slice(0, MAX_STORED_LETTERS)
    : [];
}

function persistLetters(userId, letters) {
  const compact = letters.slice(0, MAX_STORED_LETTERS).map((item) => ({
    id: truncateText(item.id, 80),
    title: truncateText(item.title, LETTER_TITLE_LIMIT),
    body: truncateText(item.body, LETTER_BODY_LIMIT),
    reason: truncateText(item.reason, 40),
    createdAt: item.createdAt
  }));
  safeSetJson(storageKey(userId, 'letters'), compact);
  return compact;
}

function normalizeMazeSource(value) {
  return ['unlock', 'generated', 'replay'].includes(value) ? value : 'unlock';
}

function normalizeMazeResult(value) {
  return ['completed', 'failed', 'timeout', 'abandoned', 'loopGuard'].includes(value) ? value : 'unknown';
}

function normalizeMazeComplexity(value) {
  return ['low', 'medium', 'high', 'extreme'].includes(value) ? value : undefined;
}

function compactMazeRun(item) {
  const template = normalizeMazeTemplate(item, item?.difficulty);
  const playedAt = item?.playedAt || item?.createdAt || new Date().toISOString();
  return {
    id: truncateText(item?.id || Date.now() + '-' + Math.random().toString(36).slice(2, 8), 80),
    playedAt,
    createdAt: playedAt,
    result: normalizeMazeResult(item?.result),
    source: normalizeMazeSource(item?.source),
    replayedFromId: item?.replayedFromId ? truncateText(item.replayedFromId, 80) : undefined,
    generatedDifficulty: normalizeMazeComplexity(item?.generatedDifficulty),
    difficulty: template?.difficulty || clampNumber(Number(item?.difficulty) || 1, 1, 5),
    durationSeconds: clampNumber(Number(item?.durationSeconds) || 0, 0, 300),
    remainingSeconds: clampNumber(Number(item?.remainingSeconds) || 0, 0, 300),
    grid: template?.grid || null,
    start: template?.start || null,
    target: template?.target || null,
    letterId: item?.letterId ? truncateText(item.letterId, 80) : undefined,
    staminaBefore: Number.isFinite(Number(item?.staminaBefore)) ? clampNumber(Number(item.staminaBefore), 0, STAMINA_MAX) : undefined,
    staminaAfter: Number.isFinite(Number(item?.staminaAfter)) ? clampNumber(Number(item.staminaAfter), 0, STAMINA_MAX) : undefined
  };
}

function loadMazeHistory(userId) {
  const primary = safeReadJson(storageKey(userId, 'maze_runs'), null);
  const legacy = safeReadJson(storageKey(userId, 'maze_history'), []);
  const history = Array.isArray(primary) ? primary : legacy;
  return Array.isArray(history)
    ? history
        .filter((item) => item && typeof item === 'object')
        .map(compactMazeRun)
        .slice(0, MAX_STORED_MAZE_HISTORY)
    : [];
}

function persistMazeHistory(userId, history) {
  const compact = history.slice(0, MAX_STORED_MAZE_HISTORY).map(compactMazeRun);
  safeSetJson(storageKey(userId, 'maze_runs'), compact);
  return compact;
}

function getFetchErrorMessage(err, t) {
  const status = err?.response?.status;
  if (status === 401 || status === 403) return t('consciousness.authError');
  if (status === 429) return t('consciousness.rateLimited');
  if (status === 503) return t('consciousness.serviceUnavailable');
  if (err?.code === 'ECONNABORTED') return t('consciousness.timeoutError');
  if (err?.message === 'invalidConcienciaPayload') return t('consciousness.invalidResponse');
  return t('consciousness.fetchingError');
}

const Conciencia = () => {
  const { user } = useContext(AuthContext);
  const userId = user?.id;
  const navigate = useNavigate();
  const { displayedImage, settings } = useAppBackground() || {};
  const { t } = useI18n();
  const { registerCommand, unregisterCommand } = useAgentCommands();
  const { refreshState } = useProgression() || {};

  const [viewState, setViewState] = useState(VIEW_STATE.LOADING_INITIAL);
  const [data, setData] = useState(EMPTY_DATA);
  const [selectedSphereText, setSelectedSphereText] = useState('');
  const [selectedEmotionText, setSelectedEmotionText] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const [rateLimitReset, setRateLimitReset] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [interactionCount, setInteractionCount] = useState(() => safeGetNumber('conciencia_interaction_count', 0));
  const [showSpecialQuap, setShowSpecialQuap] = useState(false);
  const [sphereVersion, setSphereVersion] = useState(0);
  const [rotatingMain, setRotatingMain] = useState(false);
  const [rotatingEmotion, setRotatingEmotion] = useState(false);
  const [activeMainIndex, setActiveMainIndex] = useState(0);
  const [activeSecondaryIndex, setActiveSecondaryIndex] = useState(0);
  const [activeExpansionText, setActiveExpansionText] = useState('');
  const [localStamina, setLocalStamina] = useState(STAMINA_MAX);
  const [lastQuestionLoadMs, setLastQuestionLoadMs] = useState(0);
  const [lateralQuestions, setLateralQuestions] = useState([]);
  const [letters, setLetters] = useState([]);
  const [mazeHistory, setMazeHistory] = useState([]);
  const [activeLetter, setActiveLetter] = useState(null);
  const [cooldownUntil, setCooldownUntil] = useState(null);
  const [cooldownStartedAt, setCooldownStartedAt] = useState(null);
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [activeHistoryView, setActiveHistoryView] = useState('letters');
  const [mazeMode, setMazeMode] = useState('unlock');
  const [activeMazeTemplate, setActiveMazeTemplate] = useState(null);
  const [activeMazeRun, setActiveMazeRun] = useState(null);
  const [generatedMazeDraft, setGeneratedMazeDraft] = useState(null);
  const [mazeActionError, setMazeActionError] = useState('');
  const [startingMazeId, setStartingMazeId] = useState('');
  const mazeResolutionRef = useRef(false);
  const mazeReturnViewStateRef = useRef(VIEW_STATE.READY);
  const mazeLaunchGuardRef = useRef(false);
  const staminaUpdatedAtRef = useRef(Date.now());
  const mainCycleIntervalRef = useRef(null);
  const secondaryCycleIntervalRef = useRef(null);

  const stopMainCycle = useCallback(() => {
    setRotatingMain(false);
    if (mainCycleIntervalRef.current) {
      window.clearInterval(mainCycleIntervalRef.current);
      mainCycleIntervalRef.current = null;
    }
  }, []);

  const stopSecondaryCycle = useCallback(() => {
    setRotatingEmotion(false);
    if (secondaryCycleIntervalRef.current) {
      window.clearInterval(secondaryCycleIntervalRef.current);
      secondaryCycleIntervalRef.current = null;
    }
  }, []);

  const stopAllCycles = useCallback(() => {
    stopMainCycle();
    stopSecondaryCycle();
  }, [stopMainCycle, stopSecondaryCycle]);

  const applyLocalStamina = useCallback((value, updatedAt = Date.now()) => {
    const safe = normalizeStaminaValue(value);
    const safeUpdatedAt = Number.isFinite(Number(updatedAt)) ? Number(updatedAt) : Date.now();
    staminaUpdatedAtRef.current = safeUpdatedAt;
    setLocalStamina(safe);
    if (userId) persistStamina(userId, safe, safeUpdatedAt);
    return safe;
  }, [userId]);

  const recordMazeAttempt = useCallback((result, meta = {}) => {
    if (!userId) return;
    const fallbackTemplate = activeMazeTemplate || createMazeTemplate(meta.difficulty || getMazeDifficulty({ interactionCount, lettersCount: letters.length }));
    const template = normalizeMazeTemplate(meta, meta.difficulty) || fallbackTemplate;
    const source = normalizeMazeSource(meta.source || activeMazeRun?.source || 'unlock');
    const entry = compactMazeRun({
      id: Date.now() + '-' + Math.random().toString(36).slice(2, 8),
      result: result || meta.result || 'unknown',
      source,
      replayedFromId: meta.replayedFromId || activeMazeRun?.replayedFromId,
      generatedDifficulty: meta.generatedDifficulty || activeMazeRun?.generatedDifficulty,
      difficulty: template.difficulty,
      durationSeconds: meta.durationSeconds,
      remainingSeconds: meta.remainingSeconds,
      grid: template.grid,
      start: template.start,
      target: template.target,
      letterId: meta.letterId,
      staminaBefore: meta.staminaBefore,
      staminaAfter: meta.staminaAfter,
      playedAt: new Date().toISOString()
    });
    setMazeHistory((current) => persistMazeHistory(userId, [entry, ...current]));
  }, [activeMazeRun, activeMazeTemplate, interactionCount, letters.length, userId]);

  const buildLetter = useCallback((reason, customMessage = '') => {
    const response = [selectedSphereText, selectedEmotionText].filter(Boolean).join(' - ') || t('consciousness.letterUnknownResponse');
    const question = data.question || t('consciousness.letterUnknownQuestion');
    const bodyBase = t('consciousness.letterFallbackBody', { question, response });
    const body = customMessage ? customMessage + ' ' + bodyBase : bodyBase;
    return {
      id: Date.now() + '-' + Math.random().toString(36).slice(2, 8),
      title: truncateText(t('consciousness.letterTitle'), LETTER_TITLE_LIMIT),
      body: truncateText(body, LETTER_BODY_LIMIT),
      reason,
      createdAt: new Date().toISOString()
    };
  }, [data.question, selectedEmotionText, selectedSphereText, t]);

  const storeLetterAndCooldown = useCallback((reason, message = '', nextViewState = VIEW_STATE.ZERO_ENERGY_LETTER) => {
    const letter = buildLetter(reason, message);
    const nextLetters = persistLetters(userId, [letter, ...letters]);
    const startedAt = Date.now();
    const until = getSafeCooldownUntil(DEFAULT_COOLDOWN_SECONDS);
    safeSetItem(storageKey(userId, 'cooldown_started_at'), startedAt);
    safeSetItem(storageKey(userId, 'cooldown_until'), until);
    setLetters(nextLetters);
    setActiveLetter(letter);
    setCooldownStartedAt(startedAt);
    setCooldownUntil(until);
    setShowSpecialQuap(false);
    setViewState(nextViewState);
    setStatusMessage(message || t('consciousness.letterCreated'));
  }, [buildLetter, letters, t, userId]);

  useEffect(() => {
    if (!userId) return;
    const key = storageKey(userId, 'warning_accepted');
    let hasAccepted = null;
    try {
      hasAccepted = localStorage.getItem(key);
    } catch (_) {}
    if (!hasAccepted) {
      setShowWarningModal(true);
    }
  }, [userId]);

  const handleAcceptWarning = () => {
    if (!userId) return;
    safeSetItem(storageKey(userId, 'warning_accepted'), 'true');
    setShowWarningModal(false);
  };

  const handleRejectWarning = () => {
    navigate('/');
  };

  const fetchQuestion = useCallback(async (options = {}) => {
    if (!userId) return;

    if (!options.forceNew) {
      const cached = safeReadJson('q_conciencia_current_data', null);
      if (cached && cached.question) {
        setData({
          question: cached.question || '',
          questionReflection: cached.questionReflection || '',
          opciones: Array.isArray(cached.opciones) ? cached.opciones : [],
          inspiracionOpciones: Array.isArray(cached.inspiracionOpciones) ? cached.inspiracionOpciones : []
        });
        setLateralQuestions(Array.isArray(cached.lateralQuestions) ? cached.lateralQuestions : []);
        setShowSpecialQuap(false);
        setRateLimitReset(null);
        setViewState(VIEW_STATE.READY);
        return;
      }
    }

    window.dispatchEvent(new CustomEvent('q-stamina-pulse'));
    setViewState(VIEW_STATE.LOADING_INITIAL);
    setError('');
    setSubmitStatus('');
    setStatusMessage('');
    setData(EMPTY_DATA);
    setLateralQuestions([]);
    setSelectedSphereText('');
    setSelectedEmotionText('');
    setActiveExpansionText('');
    setSphereVersion((value) => value + 1);
    const loadStartedAt = Date.now();

    try {
      const storedInteractionCount = safeGetNumber('conciencia_interaction_count', 0);
      const payload = {
        interactionCount: Number.isFinite(storedInteractionCount) ? storedInteractionCount : 0
      };
      if (options.postLetterShift === true) payload.postLetterShift = true;
      if (typeof options.avoidLastTheme === 'string' && options.avoidLastTheme.trim()) {
        payload.avoidLastTheme = options.avoidLastTheme.trim().slice(0, 120);
      }

      const res = await generateConcienciaQuestion(payload);
      const resp = res?.data || {};
      if (!resp.question || typeof resp.question !== 'string') {
        throw new Error('invalidConcienciaPayload');
      }

      setData({
        question: resp.question || '',
        questionReflection: resp.questionReflection || '',
        opciones: Array.isArray(resp.opciones) ? resp.opciones : [],
        inspiracionOpciones: Array.isArray(resp.inspiracionOpciones) ? resp.inspiracionOpciones : []
      });
      setLateralQuestions(Array.isArray(resp.lateralQuestions) ? resp.lateralQuestions.slice(0, 3) : []);
      safeSetJson('q_conciencia_current_data', {
        question: resp.question || '',
        questionReflection: resp.questionReflection || '',
        opciones: Array.isArray(resp.opciones) ? resp.opciones : [],
        inspiracionOpciones: Array.isArray(resp.inspiracionOpciones) ? resp.inspiracionOpciones : [],
        lateralQuestions: Array.isArray(resp.lateralQuestions) ? resp.lateralQuestions.slice(0, 3) : []
      });
      setShowSpecialQuap(false);
      setRateLimitReset(null);
      setLastQuestionLoadMs(Date.now() - loadStartedAt);
      setViewState(VIEW_STATE.READY);
    } catch (err) {
      console.error('Error obteniendo la pregunta:', err);
      if (err?.response?.status === 429) {
        const resetHeader = err.response.headers?.['ratelimit-reset'] || err.response.headers?.['retry-after'];
        let finalResetTime = Date.now() + 10 * 60 * 1000;
        if (resetHeader) {
          const val = parseInt(resetHeader, 10);
          if (Number.isFinite(val)) {
            if (val < 31536000) finalResetTime = Date.now() + val * 1000;
            else finalResetTime = val < 100000000000 ? val * 1000 : val;
          }
        }
        setRateLimitReset(finalResetTime);
      }
      setError(getFetchErrorMessage(err, t));
      setData(EMPTY_DATA);
      setLateralQuestions([]);
      setShowSpecialQuap(false);
      setLastQuestionLoadMs(Date.now() - loadStartedAt);
      setViewState(VIEW_STATE.ERROR);
    }
  }, [t, userId]);

  useEffect(() => {
    if (!userId) return;
    const storedStamina = readStoredStamina(userId);
    const now = Date.now();
    applyLocalStamina(computeRechargedStamina(storedStamina.value, storedStamina.updatedAt, now), now);
    const storedLetters = loadLetters(userId);
    setLetters(storedLetters);
    setMazeHistory(loadMazeHistory(userId));
    const activeCooldown = getCooldownUntil(userId);
    if (activeCooldown) {
      setCooldownStartedAt(getCooldownStartedAt(userId, activeCooldown));
      setCooldownUntil(activeCooldown);
      setActiveLetter(storedLetters[0] || {
        id: 'cooldown-fallback',
        title: t('consciousness.letterTitle'),
        body: t('consciousness.letterFallbackBody', {
          question: t('consciousness.letterUnknownQuestion'),
          response: t('consciousness.letterUnknownResponse')
        }),
        reason: 'cooldown',
        createdAt: new Date().toISOString()
      });
      setViewState(VIEW_STATE.COOLDOWN);
      return;
    }
    fetchQuestion();
  }, [applyLocalStamina, fetchQuestion, t, userId]);

  useEffect(() => {
    if (!userId) return undefined;
    const timer = window.setInterval(() => {
      setLocalStamina((current) => {
        const now = Date.now();
        const next = computeRechargedStamina(current, staminaUpdatedAtRef.current, now);
        if (Math.abs(next - current) < 0.05) return current;
        staminaUpdatedAtRef.current = now;
        persistStamina(userId, next, now);
        return next;
      });
    }, STAMINA_TICK_MS);

    return () => window.clearInterval(timer);
  }, [userId]);

  const handleSphereSelection = useCallback((side, index = null) => {
    if (side === 'left' && data.opciones.length > 0) {
      const idx = index !== null ? index : activeMainIndex;
      const opt = data.opciones[idx];
      if (opt) {
        setSelectedSphereText(opt.shortText || opt.label);
        setActiveMainIndex(idx);
      }
    } else if (side === 'right' && data.inspiracionOpciones.length > 0) {
      const idx = index !== null ? index : activeSecondaryIndex;
      const opt = data.inspiracionOpciones[idx];
      if (opt) {
        setSelectedEmotionText(opt.shortText || opt.label);
        setActiveSecondaryIndex(idx);
      }
    }
  }, [activeMainIndex, activeSecondaryIndex, data.inspiracionOpciones, data.opciones]);

  const submitCurrentResponse = useCallback(async () => {
    const combined = [selectedSphereText, selectedEmotionText]
      .filter(Boolean)
      .join(' - ');
    if (!combined) return;

    try {
      setSubmitStatus(t('consciousness.sendingResponse'));
      await postConcienciaResponse({ responses: combined });
      if (refreshState) refreshState();
      safeRemoveItem('q_conciencia_current_data');
      setSubmitStatus(t('consciousness.responseSent'));
    } catch (err) {
      console.error('Error enviando respuesta:', err);
      setSubmitStatus(t('consciousness.responseError'));
    }
  }, [selectedEmotionText, selectedSphereText, t, refreshState]);

  const handleNextQuestion = useCallback(async () => {
    const now = Date.now();
    const currentStamina = computeRechargedStamina(localStamina, staminaUpdatedAtRef.current, now);
    const staminaDrain = getConcienciaStaminaDrain(lastQuestionLoadMs);
    const newStamina = applyLocalStamina(Math.max(0, currentStamina - staminaDrain), now);
    setError('');
    setStatusMessage('');

    await submitCurrentResponse();

    const nextCount = interactionCount + 1;
    setInteractionCount(nextCount);
    safeSetItem('conciencia_interaction_count', nextCount);

    if (newStamina === 0) {
      const lastUnlockAt = safeGetNumber(storageKey(userId, 'last_maze_unlock_at'), 0);
      const unlockedRecently = lastUnlockAt > 0 && Date.now() - lastUnlockAt < UNLOCK_LOOP_GUARD_MS;
      if (unlockedRecently) {
        storeLetterAndCooldown('loopGuard', t('consciousness.loopGuardMessage'));
        return;
      }
      mazeResolutionRef.current = false;
      setShowSpecialQuap(false);
      setViewState(VIEW_STATE.ZERO_ENERGY_MAZE);
      setStatusMessage(t('consciousness.mazeIntro'));
      return;
    }

    if (nextCount > 0 && nextCount % 7 === 0) {
      setShowSpecialQuap(true);
      setViewState(VIEW_STATE.LOADING_INITIAL);
      window.setTimeout(() => {
        fetchQuestion({ forceNew: true });
      }, 3000);
    } else {
      await fetchQuestion({ forceNew: true });
    }
  }, [applyLocalStamina, fetchQuestion, interactionCount, lastQuestionLoadMs, localStamina, storeLetterAndCooldown, submitCurrentResponse, t, userId]);

  const confirmSelection = useCallback(() => {
    handleNextQuestion();
  }, [handleNextQuestion]);

  const openLetterHistory = useCallback(() => {
    setActiveHistoryView('letters');
    setHistoryPanelOpen(true);
  }, []);

  const openMazeHistory = useCallback(() => {
    setActiveHistoryView('mazes');
    setHistoryPanelOpen(true);
  }, []);

  const closeHistoryPanel = useCallback(() => {
    setHistoryPanelOpen(false);
    setMazeActionError('');
  }, []);

  const closeMazePanels = useCallback(() => {
    setHistoryPanelOpen(false);
    setMazeActionError('');
  }, []);

  const startPracticeMaze = useCallback((template, runMeta) => {
    if (mazeLaunchGuardRef.current) return;
    const normalized = normalizeMazeTemplate(template, runMeta?.difficulty);
    if (!normalized) {
      setMazeActionError(t('consciousness.mazeNoStoredMap'));
      return;
    }
    mazeLaunchGuardRef.current = true;
    window.setTimeout(() => {
      mazeLaunchGuardRef.current = false;
      setStartingMazeId('');
    }, 250);
    mazeReturnViewStateRef.current = (viewState === VIEW_STATE.ZERO_ENERGY_LETTER || viewState === VIEW_STATE.COOLDOWN)
      ? viewState
      : VIEW_STATE.READY;
    setActiveMazeTemplate(normalized);
    setActiveMazeRun(runMeta);
    setMazeMode(runMeta?.source || 'replay');
    closeMazePanels();
    setViewState(VIEW_STATE.MAZE_PLAY);
  }, [closeMazePanels, t, viewState]);

  const handleReplayMaze = useCallback((item) => {
    setStartingMazeId(item?.id || '');
    const template = normalizeMazeTemplate(item, item?.difficulty);
    if (!template) {
      setStartingMazeId('');
      setMazeActionError(t('consciousness.mazeNoStoredMap'));
      return;
    }
    startPracticeMaze(template, { source: 'replay', replayedFromId: item.id, difficulty: template.difficulty });
  }, [startPracticeMaze, t]);

  const handleGenerateMaze = useCallback((complexity = 'low') => {
    const normalizedComplexity = normalizeMazeComplexity(complexity) || 'low';
    const difficulty = MAZE_COMPLEXITY_TO_DIFFICULTY[normalizedComplexity] || 1;
    const template = createMazeTemplate(difficulty);
    setGeneratedMazeDraft({ ...template, generatedDifficulty: normalizedComplexity });
    setMazeActionError('');
  }, []);

  const handlePlayGeneratedMaze = useCallback(() => {
    const template = normalizeMazeTemplate(generatedMazeDraft, generatedMazeDraft?.difficulty);
    if (!template) {
      setMazeActionError(t('consciousness.mazeNoStoredMap'));
      return;
    }
    startPracticeMaze(template, {
      source: 'generated',
      generatedDifficulty: generatedMazeDraft.generatedDifficulty || 'low',
      difficulty: template.difficulty
    });
  }, [generatedMazeDraft, startPracticeMaze, t]);

  const handleDiscardGeneratedMaze = useCallback(() => {
    setGeneratedMazeDraft(null);
    setMazeActionError('');
  }, []);

  const handleDeleteLetter = useCallback((letterId) => {
    setLetters((current) => {
      const nextLetters = persistLetters(userId, current.filter((item) => item.id !== letterId));
      if (activeLetter?.id === letterId) setActiveLetter(nextLetters[0] || null);
      return nextLetters;
    });
  }, [activeLetter?.id, userId]);

  const handleDeleteAllLetters = useCallback(() => {
    setLetters(persistLetters(userId, []));
    setActiveLetter(null);
  }, [userId]);

  const handleDeleteMaze = useCallback((mazeId) => {
    setMazeHistory((current) => persistMazeHistory(userId, current.filter((item) => item.id !== mazeId)));
  }, [userId]);

  const handleDeleteAllMazes = useCallback(() => {
    setMazeHistory(persistMazeHistory(userId, []));
    setGeneratedMazeDraft(null);
  }, [userId]);

  const returnFromPracticeMaze = useCallback(async () => {
    const returnState = mazeReturnViewStateRef.current;
    setActiveMazeTemplate(null);
    setActiveMazeRun(null);
    setMazeMode('unlock');
    if (returnState === VIEW_STATE.ZERO_ENERGY_LETTER || returnState === VIEW_STATE.COOLDOWN) {
      setViewState(returnState);
      return;
    }
    if (data.question) {
      setViewState(VIEW_STATE.READY);
      return;
    }
    await fetchQuestion();
  }, [data.question, fetchQuestion]);

  const handlePracticeMazeComplete = useCallback(async (meta = {}) => {
    recordMazeAttempt('completed', { ...meta, ...activeMazeRun });
    setStatusMessage(t('consciousness.mazePracticeCompleted'));
    await returnFromPracticeMaze();
  }, [activeMazeRun, recordMazeAttempt, returnFromPracticeMaze, t]);

  const handlePracticeMazeFail = useCallback(async (reason, meta = {}) => {
    recordMazeAttempt(reason, { ...meta, ...activeMazeRun });
    setStatusMessage(t('consciousness.mazePracticeExited'));
    await returnFromPracticeMaze();
  }, [activeMazeRun, recordMazeAttempt, returnFromPracticeMaze, t]);

  const handleMazeComplete = useCallback(async (meta = {}) => {
    if (mazeResolutionRef.current) return;
    mazeResolutionRef.current = true;
    const now = Date.now();
    recordMazeAttempt('completed', { ...meta, source: 'unlock', staminaBefore: Math.round(normalizeStaminaValue(localStamina)), staminaAfter: LOCAL_UNLOCK_ENERGY });
    // MVP local unlock: maze/letter recovery intentionally restores only 20%, not backend-authoritative energy; passive local recharge can continue toward 100%.
    safeSetItem(storageKey(userId, 'last_maze_unlock_at'), now);
    applyLocalStamina(Math.max(computeRechargedStamina(localStamina, staminaUpdatedAtRef.current, now), LOCAL_UNLOCK_ENERGY), now);
    await fetchQuestion();
    setStatusMessage(t('consciousness.mazeCompleted'));
  }, [applyLocalStamina, fetchQuestion, localStamina, recordMazeAttempt, t, userId]);

  const handleMazeFail = useCallback((reason, meta = {}) => {
    if (mazeResolutionRef.current) return;
    mazeResolutionRef.current = true;
    recordMazeAttempt(reason, { ...meta, source: 'unlock', staminaBefore: Math.round(normalizeStaminaValue(localStamina)), staminaAfter: Math.round(normalizeStaminaValue(localStamina)) });
    const message = reason === 'timeout' ? t('consciousness.mazeTimeoutMessage') : t('consciousness.mazeAbandonedMessage');
    storeLetterAndCooldown(reason, message);
  }, [localStamina, recordMazeAttempt, storeLetterAndCooldown, t]);

  const handleReturnFromLetter = useCallback(async () => {
    if (cooldownUntil && Date.now() < cooldownUntil) return;
    safeRemoveItem(storageKey(userId, 'cooldown_started_at'));
    safeRemoveItem(storageKey(userId, 'cooldown_until'));
    setCooldownStartedAt(null);
    setCooldownUntil(null);
    const now = Date.now();
    // MVP local unlock: maze/letter recovery intentionally restores only 20%, not backend-authoritative energy; passive local recharge can continue toward 100%.
    applyLocalStamina(Math.max(computeRechargedStamina(localStamina, staminaUpdatedAtRef.current, now), LOCAL_UNLOCK_ENERGY), now);
    await fetchQuestion({
      postLetterShift: true,
      avoidLastTheme: activeLetter?.body || data.question || ''
    });
    setStatusMessage(t('consciousness.postLetterShiftMessage'));
  }, [activeLetter?.body, applyLocalStamina, cooldownUntil, data.question, fetchQuestion, localStamina, t, userId]);

  // Logic for Agent Q Commands
  const normalizeForSearch = useCallback((val = '') => String(val).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim(), []);

  const resolveOption = useCallback((options, targetIndex, targetText) => {
    if (typeof targetIndex === 'number' && options[targetIndex]) return targetIndex;
    if (targetText) {
      const normalizedTarget = normalizeForSearch(targetText);
      // Exact match
      let idx = options.findIndex(o => normalizeForSearch(o.shortText || o.label) === normalizedTarget);
      if (idx !== -1) return idx;
      // Partial match
      idx = options.findIndex(o => normalizeForSearch(o.shortText || o.label).includes(normalizedTarget));
      if (idx !== -1) return idx;
    }
    return -1;
  }, [normalizeForSearch]);

  useEffect(() => {
    if (viewState !== VIEW_STATE.READY) {
      stopAllCycles();
    }

    registerCommand('conciencia_cycle_main', () => {
      if (viewState !== VIEW_STATE.READY || !data.opciones.length) return { ok: false };
      if (mainCycleIntervalRef.current) return { ok: true };
      setRotatingMain(true);
      mainCycleIntervalRef.current = window.setInterval(() => {
        setActiveMainIndex(prev => (data.opciones.length ? (prev + 1) % data.opciones.length : 0));
      }, 1000);
      return { ok: true };
    });

    registerCommand('conciencia_cycle_secondary', () => {
      if (viewState !== VIEW_STATE.READY || !data.inspiracionOpciones.length) return { ok: false };
      if (secondaryCycleIntervalRef.current) return { ok: true };
      setRotatingEmotion(true);
      secondaryCycleIntervalRef.current = window.setInterval(() => {
        setActiveSecondaryIndex(prev => (data.inspiracionOpciones.length ? (prev + 1) % data.inspiracionOpciones.length : 0));
      }, 1000);
      return { ok: true };
    });

    registerCommand('conciencia_stop_cycle', () => {
      stopAllCycles();
      return { ok: true };
    });

    registerCommand('conciencia_set_main_option', (payload) => {
      if (viewState !== VIEW_STATE.READY || !data.opciones.length) return { ok: false };
      stopMainCycle();
      let nextIdx = activeMainIndex;
      const target = payload.targetIndex;
      const len = data.opciones.length;

      if (typeof target === 'number') nextIdx = clampNumber(target, 0, len - 1);
      else if (target === 'next') nextIdx = (activeMainIndex + 1) % len;
      else if (target === 'previous') nextIdx = (activeMainIndex - 1 + len) % len;
      else if (target === 'last') nextIdx = len - 1;

      setActiveMainIndex(nextIdx);
      return { ok: true };
    });

    registerCommand('conciencia_set_secondary_option', (payload) => {
      if (viewState !== VIEW_STATE.READY || !data.inspiracionOpciones.length) return { ok: false };
      stopSecondaryCycle();
      let nextIdx = activeSecondaryIndex;
      const target = payload.targetIndex;
      const len = data.inspiracionOpciones.length;

      if (typeof target === 'number') nextIdx = clampNumber(target, 0, len - 1);
      else if (target === 'next') nextIdx = (activeSecondaryIndex + 1) % len;
      else if (target === 'previous') nextIdx = (activeSecondaryIndex - 1 + len) % len;
      else if (target === 'last') nextIdx = len - 1;

      setActiveSecondaryIndex(nextIdx);
      return { ok: true };
    });

    registerCommand('conciencia_select_main', (payload) => {
      if (viewState !== VIEW_STATE.READY) return { ok: false };
      stopMainCycle();
      const idx = resolveOption(data.opciones, payload.targetIndex, payload.targetText);
      if (idx !== -1) {
        handleSphereSelection('left', idx);
        return { ok: true };
      }
      return { ok: false };
    });

    registerCommand('conciencia_select_secondary', (payload) => {
      if (viewState !== VIEW_STATE.READY) return { ok: false };
      stopSecondaryCycle();
      const idx = resolveOption(data.inspiracionOpciones, payload.targetIndex, payload.targetText);
      if (idx !== -1) {
        handleSphereSelection('right', idx);
        return { ok: true };
      }
      return { ok: false };
    });

    registerCommand('conciencia_scroll_secondary', () => {
      if (viewState !== VIEW_STATE.READY || !data.inspiracionOpciones.length) return { ok: false };
      stopSecondaryCycle();
      setActiveSecondaryIndex(prev => (data.inspiracionOpciones.length ? (prev + 1) % data.inspiracionOpciones.length : 0));
      return { ok: true };
    });

    registerCommand('conciencia_next_question', async () => {
      if (viewState !== VIEW_STATE.READY) return { ok: false };
      handleNextQuestion();
      return { ok: true };
    });

    return () => {
      unregisterCommand('conciencia_cycle_main');
      unregisterCommand('conciencia_cycle_secondary');
      unregisterCommand('conciencia_stop_cycle');
      unregisterCommand('conciencia_select_main');
      unregisterCommand('conciencia_select_secondary');
      unregisterCommand('conciencia_scroll_secondary');
      unregisterCommand('conciencia_next_question');
      unregisterCommand('conciencia_set_main_option');
      unregisterCommand('conciencia_set_secondary_option');
      if (mainCycleIntervalRef.current) { window.clearInterval(mainCycleIntervalRef.current); mainCycleIntervalRef.current = null; }
      if (secondaryCycleIntervalRef.current) { window.clearInterval(secondaryCycleIntervalRef.current); secondaryCycleIntervalRef.current = null; }
    };
  }, [activeMainIndex, activeSecondaryIndex, data.inspiracionOpciones.length, data.opciones.length, data.opciones, data.inspiracionOpciones, handleNextQuestion, handleSphereSelection, registerCommand, resolveOption, stopAllCycles, stopMainCycle, stopSecondaryCycle, unregisterCommand, viewState]);

  const renderReadyContent = () => {
    if (!data.question) {
      return (
        <div className="conciencia-state-panel">
          <h2>{t('consciousness.stateErrorTitle')}</h2>
          <p>{t('consciousness.invalidResponse')}</p>
          <button type="button" className="regenerate-btn" onClick={() => fetchQuestion({ forceNew: true })}>
            {t('consciousness.retry')}
          </button>
        </div>
      );
    }

    return (
      <div className="conciencia-content-wrapper">
        <div className="conciencia-text-panel">
          <h2><ReadableText text={data.question} label={t('consciousness.readQuestion')}>{data.question}</ReadableText></h2>
          {data.questionReflection && (
            <p className="question-reflection"><ReadableText text={data.questionReflection} label={t('consciousness.readReflection')}>{data.questionReflection}</ReadableText></p>
          )}
        </div>

        {lateralQuestions.map((question, index) => (
          <div
            key={question + '-' + index}
            className={'lateral-asking-dot lateral-asking-dot-' + (index + 1)}
            aria-label={question}
            tabIndex={0}
          >
            <span className="lateral-question-tooltip">{question}</span>
          </div>
        ))}

        {data.opciones.length > 0 && (
          <div className="sphere-menu-wrapper" id="sphere-left">
            <SphereMenu
              key={'left-sphere-' + data.question + '-' + sphereVersion}
              options={data.opciones.map((op) => ({
                  id: op.value || op.shortText,
                  content: op.shortText || op.label
              }))}
              onSelect={setSelectedSphereText}
              onActiveIndexChange={(index) => {
                  if (data.opciones[index]) {
                      setActiveExpansionText(data.opciones[index].expansionText || '');
                  }
              }}
              radius={4}
              style={{ height: '100%' }}
              rotating={rotatingMain}
              initialIndex={activeMainIndex}
              onUserInteract={stopMainCycle}
            />
          </div>
        )}

        {data.inspiracionOpciones.length > 0 && (
          <div className="emotion-sphere-wrapper">
            <div id="sphere-right" style={{ width: '180px', height: '180px', margin: '0 auto' }}>
              <EmotionSphere
                key={'right-sphere-' + data.question + '-' + sphereVersion}
                options={data.inspiracionOpciones.map((opt) => ({
                    id: opt.value || opt.shortText,
                    content: opt.shortText || opt.label
                }))}
                onSelect={setSelectedEmotionText}
                onActiveIndexChange={(index) => {
                    if (data.inspiracionOpciones[index]) {
                        setActiveExpansionText(data.inspiracionOpciones[index].expansionText || '');
                    }
                }}
                radius={2.5}
                rotating={rotatingEmotion}
                initialIndex={activeSecondaryIndex}
                onUserInteract={stopSecondaryCycle}
              />
            </div>
          </div>
        )}

        {activeExpansionText && (
          <div className="sphere-expansion-text">
              <ReadableText text={activeExpansionText} label={t('consciousness.readExpansion')}>
                {activeExpansionText}
              </ReadableText>
          </div>
        )}

        <div className="conciencia-controls-panel">
          <div className="response-area">
            <textarea
              id="consciousness-feedback-area"
              placeholder={t('consciousness.responsePlaceholder')}
              value={[selectedSphereText, selectedEmotionText].filter(Boolean).join(' - ')}
              readOnly
              rows={3}
            />
          </div>

          <button id="btn-generate-consciousness" onClick={handleNextQuestion} className="regenerate-btn">{t('consciousness.nextQuestion')}</button>

          {submitStatus && <p className="submit-status">{submitStatus}</p>}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (viewState === VIEW_STATE.LOADING_INITIAL) {
      return (
        <MentalLoadScreen
          loading={true}
          message={showSpecialQuap ? t('consciousness.quapReloading') : t('consciousness.loadingQuestion')}
        />
      );
    }

    if (viewState === VIEW_STATE.READY) return renderReadyContent();

    if (viewState === VIEW_STATE.ZERO_ENERGY_MAZE || viewState === VIEW_STATE.MAZE_PLAY) {
      const isPracticeMaze = viewState === VIEW_STATE.MAZE_PLAY;
      return (
        <ConcienciaMazeGame
          difficulty={isPracticeMaze ? activeMazeTemplate?.difficulty : getMazeDifficulty({ interactionCount, lettersCount: letters.length })}
          mazeTemplate={isPracticeMaze ? activeMazeTemplate : null}
          mode={isPracticeMaze ? mazeMode : 'unlock'}
          onComplete={isPracticeMaze ? handlePracticeMazeComplete : handleMazeComplete}
          onFail={isPracticeMaze ? handlePracticeMazeFail : handleMazeFail}
          t={t}
        />
      );
    }

    if (viewState === VIEW_STATE.ZERO_ENERGY_LETTER || viewState === VIEW_STATE.COOLDOWN) {
      return (
        <ConcienciaLetterPanel
          letter={activeLetter}
          letters={letters}
          cooldownUntil={cooldownUntil}
          cooldownStartedAt={cooldownStartedAt}
          currentStamina={Math.round(normalizeStaminaValue(localStamina))}
          staminaMax={STAMINA_MAX}
          mazeHistory={mazeHistory}
          onReturn={handleReturnFromLetter}
          onOpenHistory={openLetterHistory}
          showHistory={historyPanelOpen && activeHistoryView === 'letters'}
          onCloseHistory={closeHistoryPanel}
          onOpenMazeHistory={openMazeHistory}
          showMazeHistory={historyPanelOpen && activeHistoryView === 'mazes'}
          onCloseMazeHistory={closeHistoryPanel}
          onReplayMaze={handleReplayMaze}
          onDeleteMaze={handleDeleteMaze}
          onDeleteAllMazes={handleDeleteAllMazes}
          onDeleteLetter={handleDeleteLetter}
          onDeleteAllLetters={handleDeleteAllLetters}
          onGenerateMaze={handleGenerateMaze}
          onPlayGeneratedMaze={handlePlayGeneratedMaze}
          onDiscardGeneratedMaze={handleDiscardGeneratedMaze}
          generatedMazeDraft={generatedMazeDraft}
          mazeActionError={mazeActionError}
          startingMazeId={startingMazeId}
          t={t}
        />
      );
    }

    return (
      <div className="conciencia-state-panel">
        <h2>{t('consciousness.stateErrorTitle')}</h2>
        <p>{error || t('consciousness.fetchingError')}</p>
        <button type="button" className="regenerate-btn" onClick={() => fetchQuestion({ forceNew: true })}>
          {t('consciousness.retry')}
        </button>
      </div>
    );
  };

  const displayedStamina = Math.round(normalizeStaminaValue(localStamina));

  if (!userId) {
    return <p>{t('consciousness.loginRequired')}</p>;
  }

  return (
    <>
      {settings?.enabled && displayedImage && <ProfileBackground imageData={displayedImage} interactive={false} />}
      <div id="consciousness-main" className="conciencia-container" style={{ position: 'relative', zIndex: 2 }}>
      {showWarningModal && (
        <div className="conciencia-modal-overlay">
          <div className="conciencia-modal">
            <h2>{t('consciousness.importantNotice')}</h2>
            <p>
              {t('consciousness.warning1')}{' '}
              {t('consciousness.warning2')}
            </p>
            <p><strong>{t('consciousness.discretion')}</strong></p>
            <div className="conciencia-modal-actions">
              <button className="modal-btn-reject" onClick={handleRejectWarning} title={t('consciousness.exitMain')}>✕</button>
              <button className="modal-btn-accept" onClick={handleAcceptWarning} title={t('consciousness.enter')}>✓</button>
            </div>
          </div>
        </div>
      )}

      <div className="conciencia-header-row">
        <h1>{t('consciousness.title')}</h1>
        <div className="conciencia-header-tools">
          <div className={'conciencia-stamina-meter' + (displayedStamina < STAMINA_MAX ? ' recharging' : '')} title={t('consciousness.stamina', { value: displayedStamina })}>
            <div className="stamina-meter-track">
               <div
                 className="stamina-meter-fill"
                 style={{
                   width: displayedStamina + '%',
                   background: displayedStamina > 40 ? '#00ffaa' : displayedStamina > 20 ? '#ffcc00' : '#ff4444'
                 }}
               />
            </div>
            <span className="stamina-meter-label">{displayedStamina}%</span>
          </div>
          <button
            type="button"
            className="conciencia-letter-history-btn"
            onClick={openLetterHistory}
            aria-label={t('consciousness.letterHistoryAria')}
            title={t('consciousness.letterHistoryAria')}
          >
            <svg className="conciencia-campfire-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M7 20h10" />
              <path d="M8 18l8 3" />
              <path d="M16 18l-8 3" />
              <path d="M12 18c-2 0-3.5-1.4-3.5-3.2 0-1.4.8-2.5 1.8-3.6.7-.8 1.4-1.7 1.5-3.2 1.9 1.2 3.7 3.2 3.7 5.9 0 2.4-1.5 4.1-3.5 4.1z" />
              <path d="M12.2 17c-.9 0-1.6-.7-1.6-1.7 0-.7.4-1.2.8-1.8.4-.5.7-.9.7-1.6 1 .7 1.8 1.8 1.8 3.1 0 1.2-.7 2-1.7 2z" />
            </svg>
          </button>
        </div>
      </div>

      {statusMessage && <p className="submit-status">{statusMessage}</p>}
      {error && <p className="error">{error}</p>}

      {rateLimitReset && (
        <RateLimitWarning
            resetTime={rateLimitReset}
            onReset={() => {
                setRateLimitReset(null);
                fetchQuestion();
            }}
        />
      )}

      {renderContent()}

      {historyPanelOpen && (viewState !== VIEW_STATE.ZERO_ENERGY_LETTER && viewState !== VIEW_STATE.COOLDOWN) && (
        <ConcienciaLetterPanel
          letter={null}
          letters={letters}
          cooldownUntil={0}
          cooldownStartedAt={0}
          currentStamina={Math.round(normalizeStaminaValue(localStamina))}
          staminaMax={STAMINA_MAX}
          mazeHistory={mazeHistory}
          onReturn={closeHistoryPanel}
          onOpenHistory={openLetterHistory}
          showHistory={historyPanelOpen && activeHistoryView === 'letters'}
          onCloseHistory={closeHistoryPanel}
          onOpenMazeHistory={openMazeHistory}
          showMazeHistory={historyPanelOpen && activeHistoryView === 'mazes'}
          onCloseMazeHistory={closeHistoryPanel}
          onReplayMaze={handleReplayMaze}
          onDeleteMaze={handleDeleteMaze}
          onDeleteAllMazes={handleDeleteAllMazes}
          onDeleteLetter={handleDeleteLetter}
          onDeleteAllLetters={handleDeleteAllLetters}
          onGenerateMaze={handleGenerateMaze}
          onPlayGeneratedMaze={handlePlayGeneratedMaze}
          onDiscardGeneratedMaze={handleDiscardGeneratedMaze}
          generatedMazeDraft={generatedMazeDraft}
          mazeActionError={mazeActionError}
          startingMazeId={startingMazeId}
          t={t}
        />
      )}

      <div style={{ display: 'none' }}>
        <button id="proxy-btn-sphere-left" onClick={() => handleSphereSelection('left')} />
        <button id="proxy-btn-sphere-right" onClick={() => handleSphereSelection('right')} />
        <button id="proxy-btn-sphere-lock" onClick={() => confirmSelection()} />
        <button id="btn-sphere-rotate" onClick={() => setRotatingMain(prev => !prev)} />
        <button id="btn-toggle-rotate-main" onClick={() => setRotatingMain(prev => !prev)} />
        <button id="btn-toggle-rotate-emotion" onClick={() => setRotatingEmotion(prev => !prev)} />
        <button id="btn-stop-rotation" onClick={() => { setRotatingMain(false); setRotatingEmotion(false); }} />
        <button id="btn-refresh-question" onClick={() => fetchQuestion({ forceNew: true })} />
      </div>

      <div className="conciencia-footer-action">
        <button
          className="conciencia-about-btn"
          onClick={() => navigate('/conciencia/about')}
          title={t('consciousness.moreInfo')}
        >
          +
        </button>
      </div>
      </div>
    </>
  );
};

export default Conciencia;
